import type { FileRecord } from "@cohesive/api-client";

/**
 * Return the direct (non-recursive) children of a folder from a flat
 * record list. A direct child is a record whose path:
 *   - starts with the folder's path (so we stay inside the folder),
 *   - has at least one remaining character beyond that prefix (exclude
 *     the folder marker itself if it's in `records`), and
 *   - contains no further `/` once a trailing `/` is stripped — i.e.
 *     lives exactly one level below the folder.
 *
 * `folderPath` is expected to end with `/` for a folder (including
 * `""` for the workspace root). The `cleaned.length > 0` guard matters
 * for an edge case: a record at root whose path is literally `"/"`
 * would otherwise slip through as a "direct child" of the root and
 * surface as a phantom item in the grid.
 *
 * Kept in its own module (alongside `folderDelete.ts` /
 * `folderMove.ts`) so both the detail page and the move-conflict
 * resolver share one canonical implementation — the two had drifted
 * before, with the detail-page copy missing the length guard.
 */
export function getDirectChildren(
  records: FileRecord[],
  folderPath: string,
): FileRecord[] {
  return records.filter((r) => {
    if (!r.path.startsWith(folderPath)) return false;
    const remainder = r.path.slice(folderPath.length);
    if (!remainder) return false;
    const cleaned = remainder.endsWith("/")
      ? remainder.slice(0, -1)
      : remainder;
    return cleaned.length > 0 && !cleaned.includes("/");
  });
}
